import { IDeviceModel, hd } from '@nx-fe/services';
import { BehaviorSubject, Observable, } from 'rxjs';
import { ColorQuality, DeviceScale, IInitResponsePacket, ILoginResponsePacket, IMonitorInfo, IScreenPacket, ISystemInfo } from './definitions';
import { BaseMessage } from './messages';
import { MemoryBuffer } from './utils/memoryBuffer';
import { IWebSocketProvider } from './webSocket';

export type MessageCallback = (message: MemoryBuffer | BaseMessage) => void;

export interface IRemoteControlConnection {
    connect(startPoppedOut?: boolean): void;
    disconnect(): void;
    isConnected(): boolean;
    getWebSocket(): WebSocket;
    getScale(): number;
    // getQuality(): number;
    // getColorDepth(): number;
    // applyChange(forceApply: boolean, scale?: number, quality?: number, colorDepth?: number): void;
}

export interface Point {
    x: number;
    y: number;
}

export interface IDeviceViewHandler {
    prepare(): void;
    cleanup(): void;
    scale(scale: number, forceApply?: boolean): void;
    autoScaleEnabled(): boolean;
    getViewScale(increment?: number): number;
    fullyVisible(): boolean;
    offsetView(dx: number, dy: number): void;
    convertToDeviceCoordinates(x: number, y: number): Point;
    takeScreenshot(includeSkin: boolean): HTMLCanvasElement;
    setCanvasCursor(cursor: string): void;
    stopVideoPlayer(): void;
    updateBuffer(buf: Uint8Array, isKeyFrame: boolean): void;
    queueBuffer(buf: Uint8Array): void;
    buildMediaSource(): MediaSource;
    sendAck(): void;
    onUpdate$: Observable<void>;
}

export interface ElementRect {
    left: number;
    top: number;
    right: number;
    bottom: number;
    height: number;
    width: number;
    x: number;
    y: number;
}

export enum AnnotationDrawingMode {
    BothUsers = 0x2, // All
    AdminOnly = 0x1, // AdminOnly
}

export enum AnnotationOperatingMode {
    Annotation = 0x1, // Annotation
    Whiteboard = 0x2, // Whiteboard
}

export enum AgentRecordingStopReason {
    Unspecified = -1,
    RequestedByUser = 0,
    TimeLimit = 1,
    SizeLimit = 2
}

export enum SotiAssistStatus {
    SasVideoFolderCreationFailed = 0x1,         // SAS_VIDEO_FOLDER_CREATION_FAILED:
    SasVideoEncodingServerOccupied = 0x2,       // SAS_VIDEO_ENCODING_SERVER_OCCUPIED: 0x02,
    SasVideoMaxDurationReached = 0x4,           // SAS_VIDEO_MAX_DURATION_REACHED: 0x04,
    SasVideoAttachmentCreationFailed = 0x8,     // SAS_VIDEO_ATTACHMENT_CREATION_FAILED: 0x08,
    SasDownloadLinkCreationFailed = 0x10,       // SAS_DOWNLOAD_LINK__CREATION_FAILED: 0x10,
    SasVideoRecordingEmpty = 0x20,              // SAS_VIDEO_RECORDING_EMPTY: 0x20,
    SasVideoFolderPathTooLong = 0x40,           // SAS_VIDEO_FOLDER_PATH_TOO_LONG: 0x40,
    SasLogFileAttachmentCreationFailed = 0x80,  // SAS_LOG_ATTACHMENT_CREATION_FAILED: 0x80
    SasLogFileUnavailableOnAgent = 0x100        // SAS_LOG_FILE_UNAVAILABLE_ON_AGENT: 0x100
}

export interface ButtonInformation {
    id: string;
    bottom: number;
    left: number;
    right: number;
    top: number;
    keyValue: number;
    type: string;
}

export interface IAppButtonInfo {
    id: string;
    type: string;
    keyValue: number;
    area: ElementRect;
    text: string;
}

export interface ISkinInformation {
    id: string;
    deviceWidth: number;
    deviceHeight: number;
    rotation: number;
    hasNotchImage: boolean;
    screen: ElementRect;
    buttonIndexes: Array<ButtonInformation>;
    applicationButtonIndexes?: Array<IAppButtonInfo>;
}

export interface SOTIHTMLCanvasElement extends HTMLCanvasElement {
    ctx: IDeviceContext;
    screenHeight: number;
    screenWidth: number;
    cx: number;
    cy: number;
}

export interface IDeviceCanvas {
    /** Will be true after the canvas is ready to be used */
    loaded: boolean;
    /** work as the same with previous ctx.canvas.size */
    width: number;
    /** work as the same with previous ctx.canvas.size */
    height: number;
    /** RC Canvas (replaces ctx.canvas) */
    element: SOTIHTMLCanvasElement;
    init(options: Partial<IDeviceCanvas>): void;
    clientWidth: number;
    clientHeight: number;
}

export interface IDeviceContextSkinInfo {
    /** Will be true after receiving the skin information and the skin image */
    loaded: boolean;
    width: number;
    height: number;
    rotation: number;
    /** The skin's image we render */
    image: HTMLImageElement;
    /** Irregular screens within skins are screens that are not rectangular */
    isIrregular: boolean;
    /** The screen area within the skin */
    screen: {
        width: number;
        height: number;
        top: number;
        left: number;
        bottom: number;
        right: number;
    };
}

// Integrate RemoteControlContext (streaming RC) with DeviceContext (standard RC)
export interface IBaseDeviceContext {
    osType: number;
    scale: number;            // the actual scale, the ratio of device view size to device physical screen size
    rotation: number;         // the current device screen orientation
    screenWidth: number;      // device screen width when rotation == 0
    screenHeight: number;     // device screen height when rotation == 0
    disableMouseInput: boolean;
    deviceSupportsScreenProjection: boolean;
    imageQuality: number;

    /**
     * Based on the remote-control.services.ts setupRemoteControl(deviceModel: IDeviceModel) function,
     * no matter standard or streaming RC, both of them will have following three properties as part of remote control data
     */
    remoteControlSharedData: hd.IRemoteControlModel;    // returned from IDeviceService.getRemoteControlData()
    remoteControlConnection: IRemoteControlConnection;  // connection to device (WebSocket to DS in case of video streaming, or to RC Service otherwise)
    videoStreaming: boolean;  // true if this context is using video streaming
}

export interface ISystemInfoUI {  update(info: ISystemInfo): void }

export interface IRcDevice {
    /** Will be true after receiving information about the device (either InitResponse or LoginResponse) */
    loaded: boolean;
    osType: number;
    /** Width is always "the edge closest to the floor" (no matter what the rotation is) */
    width: number;
    height: number;
    /** Rotation from 0 (device default position) to 3, turning counter clockwise */
    rotation: number;
    /** The scale the device is sending us images (this info is coming on every screenPacket) */
    scale: number;
    supportsScreenProjection: boolean;
    /** True once we received the first full screen message */
    fullscreenLoaded: boolean;
    fullScreenLoaded$: BehaviorSubject<boolean>;
}

export interface IDeviceClient {
    /** The current client rotation */
    rotation: number;
    /** The target client rotation we want to get at, (used for animation) */
    targetRotation: number;
    // used in RC Refactoring for 2024.0.4 - Phase 1
    scale?: number; // The scale the image needs to be at to fit within the canvas
    scaleFactor?: number; // The factor between the Device scale and the client scale
    restartInfo?: {  // Information used to track what was the last restart message configuration we sent
        scale: number;
        bitDepth: number;
        imageQuality: number;
    }
}

export interface IDeviceContext extends IBaseDeviceContext {
    /** What we know about the real device, updated with initResponse, loginResponse, and every screenPacket received */
    device: IRcDevice;
    skin: IDeviceContextSkinInfo;
    canvas: IDeviceCanvas;
    client: IDeviceClient;
    setRotation(rotation: number): void;
    transfer(oldContext: IDeviceContext): void;
    clearSkin(): void;
}

// only used inside the TransformationHandler class
export interface ScaleResult {
    scale: number;
    clientScale: number;
    clientSideRotation: number;
}

export interface IDeviceScreenTransformService {
    getAutoZoomScale(): number;
    getCustomZoomScale(): number;
    getTHTargetScale(): DeviceScale;
    getAutoZoomActive(): boolean;
    getScreenScale(): number;
    getScreenToSkinRatio(context?: IDeviceContext): number;
    getCustomScale(): number;
    setTargetScale(scale: DeviceScale | string | number): void;
    setCustomScale(scale: number): void;
    isAutoZoomActive(): boolean;
    checkIfAutoZoomAndGetScale(): ScaleResult;
    updateAfterTransfer(): void;
    updateSkin(data: any): void;
    updateTransform(): void;
    updateTransformAndScreen(screenPacket: IScreenPacket): void;
    subscribeToEndOfTransformations(callback: Function): void;
    unsubscribeToEndOfTransformations(callback: Function): void;
    makeCanvasVisible(): void;
    refreshCanvas(): void;
    autoZoomToCurrentViewSize(noAnimation?: boolean): void;
    clientSideRotate(clockwise: boolean): void;
    transferPanZoom(oldRc: any): void;
    shutdown(): void;
    offsetView(x?: number, y?: number): void;
}

export interface IDeviceContextHandler {
    /** Returns a clone of the current Device Context */
    currentContext(clone?: boolean): IDeviceContext;
    /** Returns true if we received all information required to consider the context ready (canvas, init/login responses, skin info, etc) */
    isContextReady(): boolean;
    /** Publishes deviceContext ready changes */
    contextReady$: Observable<boolean>;
    /** Publishes weather the context has all the information required to send restart messages */
    contextReadyForFirstRestartMessage$: Observable<boolean>;
    /** Publishes a messages every time the device rotation changes */
    rotationChanged$: Observable<number>;
    /** Transfers the canvas between windows */
    transfer(deviceContext: IDeviceContext): void;
    /** Updates the device context with information received in the InitResponse*/
    updateWithInitResponse(initResponse: IInitResponsePacket): void;
    /** Updates the device context with information received in the LoginResponse*/
    updateWithLoginResponse(loginResponse: ILoginResponsePacket): void;
    /** Updates the device context with information received about the skin (SkinInformation and SkinImage)*/
    updateWithSkinInformation(skinInformation: ISkinInformation, skinImage: HTMLImageElement): void;
    /** Updates the device context with information about the canvas*/
    updateWithCanvasChanges(width: number, height: number): void;
    /** Updates the device context with information about screen packets. Called every time we receive a full or partial screen update*/
    updateWithScreenPacket(screenPacket: IScreenPacket): void;
    /** Updates the device context with information about the client rotation */
    updateWithClientRotation(clientRotation: number, clientTargetRotation: number): void;
    /** Called to keep track of parameters of the last restart message we sent to the agent */
    updateWithRestartInfo(scale: number, bitDepth: ColorQuality, imageQuality: number);
    /** Used to clear the restart message tracking, so a new message can be sent after this method is called */
    updateWithSessionEnd(): void;
    /** Updates the device context with information about the client scale */
    updateWithClientScale(clientScale: number): void;

    updateWithNewMonitor(monitor: IMonitorInfo): void;
}

export interface InstallParameters {
    provider: IWebSocketProvider;
    host: string;
}

export interface ResponseData {
    AgentVersionHigh: number;
    AgentVersionLow: number;
    CompressionLevel: number;
    ConnectionTimeout: number;
    DeviceId: string;
    DeviceName: string;
    DeviceKind: string;
    DisableUserDisplay: boolean;
    DisableUserIntraction: boolean;
    Host: string;
    NoActivityTimeout: number;
    RemoteControlSecurityToken: string;
    Sensitivity: number;
    SessionId: string;
    SkinName: string;
    UserName: string;
    SotiAssistDeviceId: string;
}

export enum TransferOperation {
    transfer,    // transfer an existing RC session with its UI state between two windows
    start      // transferring the connection (not UI state, zoom, etc) from main window to a popup
}

export interface ScreenshotOptions {
    name: string;
    type: string;
    quality: string;
    bpp: string;
    skin: boolean;
    attachment: boolean;
    associatedDeviceName: string;
    device: IDeviceModel;
    fileName?: string;
    fileObject?: any;
}

export type CallbackType = (...args: any[]) => void;
export type DataCallbackType = (data) => void;
export type ErrorCallbackType = (err: number) => void;
export type DataErrorCallbackType = (data: any, err: number) => void;
export type FileCallbackType = (name: string, err: number) => void;
export type FileDataCallbackType = (data: Uint8Array, err: number) => void;

// export interface ISecurityContext {
//     RemoteControlInput: boolean;
//     RemoteControlViewTasks: boolean;
//     RemoteControlViewFiles: boolean;
//     RemoteControlViewSystemInfo: boolean;
//     RemoteControlCommands: boolean;
//     RemoteControlWebConsole: boolean;
//     initialize(permissions: number): void;
// };

export interface IDeviceInput {
    deviceContext: IBaseDeviceContext;
    sendSkinKey(key: number): void;
    // sendButtonEvent(button): void;
    sendKeyboardKey(keyCode: number, shift: boolean, alt: boolean, control: boolean, num: boolean): void;
    registerKeyInputListeners(): void;
    unregisterKeyInputListeners(): void;
    registerMouseInputListeners(canvas: HTMLElement): void;
    unregisterMouseInputListeners(canvas: HTMLElement): void;
    setSkinController(skinController);
    getSkinController();
    setZoomController(zoomingController);
}

export interface IDeviceCanvasRender {
    renderSkin(): void;
    renderImage(data: any): void;
    renderBuffer(data: any, fullscreen: boolean): void;
    init(): void;
    clear(): void;
    getCanvasImage(bIncludeSkin: boolean): HTMLCanvasElement;
    getCanvasRotation(): number;
    deviceHasSkin(): boolean;
    refresh2dCanvas(): void;
}

export interface IRcAppUI {
    init(deviceCanvas: IDeviceCanvasRender): void;
    setAnnotationTool(tool: any): void;
    setAnnotationCursorColor(tool: string): void;
    setAnnotationCursorSize(width: number): void;
    showAnnotationsToolHint(show: boolean): void;
    disableMouseInput(value: boolean): void;
    setOsType(osType: number): void;
    displayInProgressScreen(): void;
    getDeviceBitDepth(): number;
    setDeviceBitDepth(value: number): void;
    getDeviceImageQuality(): number;
    setSpinnerCallback(callback: Function): void;
    takeScreenshot(options: ScreenshotOptions): void;
    setCurrentMonitorRotation(rotation: number);
    setAgentRecordingVideoFrameRate(videoFrameRate: number);
    setAgentRecordingAudioBitRate(audioBitRate: number);
    setSystemInfoUI(component: ISystemInfoUI);
    getSystemInfoUI(): ISystemInfoUI;
    setDeviceImageQuality(value: number): void;
    // transfer(oldRc: RcContext, transferOperation: TransferOperation): void;
    setAgentRecordingScreenWidth(screenWidth: number);
    setAgentRecordingScreenHeight(screenHeight: number);
    setAgentRecordingVideoBitRate(videoBitRate: number);
    setAgentRecordingAudioSamplingRate(audioSamplingRate: number);
    hideInProgressScreen();
}
